/* AstroSong — Astro Window engine
 * ─────────────────────────────────
 * Reads the inlined precomputed JSON (#astro-windows-data) and exposes a
 * minimal API on window.AstroWindow:
 *
 *   pickActive()                  → next event with peak > now (tie-break by
 *                                   priority desc if two peaks < 1h apart)
 *   formatDate(peakIso)           → "June 15" (Intl, en-US)
 *   formatTitle(event, n, isSelf) → { prefix, em, suffix }
 *   formatSubtitle(event, ...)    → { prefix, core1, em, core2 }
 *   formatScarcity(event)         → { core1, em, core2 }
 *   formatStickyText(event)       → { prefix, em, suffix }
 *   formatIntroEyebrow(event)     → string
 *   formatIntroTitle(event)       → { core, em }
 *
 * The JSON payload is generated by scripts/precompute-astro-windows.mjs
 * (1×/year run) — each event already carries marker_title, marker_short,
 * adjective. So no business-logic lookup happens here, just parsing and
 * DOM helpers.
 *
 * Loaded synchronously before the inline section JS that consumes it.
 */
(function () {
  'use strict';

  var ONE_HOUR_MS = 3600000;

  /* Parse inlined JSON once at load. If the payload is missing or malformed,
     allEvents stays empty and pickActive returns null — the caller decides
     the fallback. */
  var allEvents = [];
  var dataEl = document.getElementById('astro-windows-data');
  if (dataEl) {
    try {
      var payload = JSON.parse(dataEl.textContent.trim());
      allEvents = (payload && payload.events) || [];
    } catch (e) {
      if (window.console && console.warn) {
        console.warn('AstroWindow: failed to parse inlined data', e);
      }
    }
  }

  /* Strict next-peak-ascending pickActive. Tie-break by priority desc only
     when the two soonest events fall within 1h of each other (e.g. a lunar
     eclipse and a same-day full moon — already deduped at precompute, but
     belt-and-suspenders). */
  function pickActive() {
    var now = Date.now();
    var future = [];
    for (var i = 0; i < allEvents.length; i++) {
      var peakMs = Date.parse(allEvents[i].peak);
      if (!isNaN(peakMs) && peakMs > now) {
        future.push({ event: allEvents[i], peakMs: peakMs });
      }
    }
    if (!future.length) return null;
    future.sort(function (a, b) { return a.peakMs - b.peakMs; });
    if (future.length >= 2 && (future[1].peakMs - future[0].peakMs) < ONE_HOUR_MS) {
      var p0 = future[0].event.priority || 0;
      var p1 = future[1].event.priority || 0;
      if (p1 > p0) return future[1].event;
    }
    return future[0].event;
  }

  /* "June 15" using viewer's en-US locale formatting. The customer is always
     reading in English (single-market product). The PEAK itself is UTC so
     the displayed date may shift by ±1 day for far-east timezones; this is
     acceptable since the countdown anchors the real urgency, not the
     calendar label. */
  function formatDate(peakIso) {
    try {
      var d = new Date(peakIso);
      return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' }).format(d);
    } catch (e) {
      return '';
    }
  }

  /* Strip the leading preposition (as / at / under) from a marker_title so
     that only the actual event phrase ends up in the right slot when needed.
     Kept around for legacy callers / future use. */
  function stripLeadingPreposition(marker) {
    if (!marker) return { lead: '', rest: '' };
    var m = marker.match(/^(as|at|under)\s+(.+)$/i);
    if (m) return { lead: m[1] + ' ', rest: m[2] };
    return { lead: '', rest: marker };
  }

  /* Two possessive slots are tracked through every customer-facing string
     (Lucas 2026-06-05 doctrine):
       who.chart  = name-anchored possessive for the 1st mention of chart /
                    AstroSong on a surface (identity anchor — "your" for
                    Self, "Cécile's" for Gift+name, "their" as fallback).
       who.person = pronoun-style possessive for subsequent in-sentence
                    references (resonance clause, payoff AstroSong) — "your"
                    for Self, "her"/"his" inferred from gendered relationship
                    label when no special pronouns are flagged, "their" when
                    the buyer ticked the "Include specific pronouns" checkbox
                    (signal-only, we never parse the free-text) or when the
                    label is gender-neutral (Partner/Friend/Other).
     Built once at render time via buildWho(state). Defaulting to Self
     {your, your} keeps backward callers safe. */
  function ensureWho(who) {
    if (who && typeof who === 'object' && who.chart) return who;
    return { chart: 'your', person: 'your' };
  }

  /* Recipient labels mapped to inferred pronouns when no explicit
     pronouns are flagged at step 1. The lists mirror the buttons in
     as-song-builder.liquid step 1 exactly. Anything not listed (Partner,
     Friend, Other, or an unknown future label) falls through to "their". */
  var FEMALE_LABELS = ['My Wife', 'My Mom', 'My Sister', 'My Daughter'];
  var MALE_LABELS   = ['My Husband', 'My Dad', 'My Brother', 'My Son'];

  function buildWho(state) {
    state = state || {};
    var recipient = state.recipient || '';
    var firstName = (state.firstName || '').toString().trim();
    var pronounToggle = !!state.pronounToggle;

    if (recipient === 'Myself') {
      return { chart: 'your', person: 'your' };
    }

    var chart = firstName ? (firstName + '’s') : 'their';

    var person;
    if (pronounToggle) {
      person = 'their';
    } else if (FEMALE_LABELS.indexOf(recipient) !== -1) {
      person = 'her';
    } else if (MALE_LABELS.indexOf(recipient) !== -1) {
      person = 'his';
    } else {
      person = 'their';
    }

    return { chart: chart, person: person };
  }

  /* Title — builds ON the cinematic's final line (doctrine 07-09: cinematic
     phrases are carved foundations, never repeated — the next screen builds
     on them). Cinematic promised "your new life begins"; the hero CONFIRMS
     the AstroSong as the thing that opens it. "Let" = the brand verb of the
     whole builder (Let my stars decide / speak / know): she has nothing to
     do, she allows. Gold em on "new chapter" — her intake language (14x in
     the bank: "facing a new chapter alone"). */
  function formatTitle(event, who) {
    who = ensureWho(who);
    return {
      prefix: 'Let ' + who.chart + ' AstroSong open this ',
      em: 'new chapter',
      suffix: ' of ' + who.person + ' life'
    };
  }

  /* Build a clean declarative opener from the event marker. marker_title in
     the precomputed JSON carries a leading preposition + optional "the"
     ("as Venus enters Leo", "under the Taurus New Moon", "at the Summer
     Solstice", "as Mercury turns back through Cancer"). Strip both so the
     phrase reads naturally as the start of a sentence followed by "on
     [date]":
       "as Venus enters Leo"          → "Venus enters Leo"
       "under the Taurus New Moon"    → "Taurus New Moon"
       "at the Summer Solstice"       → "Summer Solstice"
       "as Mercury turns back ..."    → "Mercury turns back ..."  (sign kept)
     Capitalize the first letter to start the sentence cleanly. */
  function eventOpener(event) {
    var marker = (event && (event.marker_title || event.marker_short)) || '';
    var m = marker.match(/^(?:as|at|under|in)\s+(?:the\s+)?(.+)$/i);
    var stripped = m ? m[1] : marker;
    if (!stripped) return '';
    return stripped.charAt(0).toUpperCase() + stripped.slice(1);
  }

  /* Subtitle — the timestamp (Lucas 07-09): deadline + permanent bonus in
     one clear line. Pote-test guardrail: the thing that expires is THE
     WINDOW; the thing that stays is the energy IN the song. The song never
     expires. The 50%% offer lives in the offer section, not here. */
  function formatSubtitle(event, who, chart) {
    who = ensureWho(who);
    var dateStr = formatDate(event.peak);
    /* Short (Lucas 07-09: 3 lines = nobody reads) with the event NAMED.
       marker_short is mixed-form ("Venus enters Virgo" / "Taurus New Moon"):
       nominalise verbals (enters -> in) and re-add "the" only when the
       original marker_title carried it (under/at/in the ...). */
    var evName = (event.marker_short || '').replace(/\senters\s/i, ' in ');
    var evArt = /^(?:under|at|in)\s+the\s/i.test(event.marker_title || '') ? 'the ' : '';
    return {
      prefix: '',
      core1: 'Reveal it before ' + dateStr + ', while ',
      em: evArt + evName,
      core2: '’s ' + (event.adjective || '') + ' energy peaks.'
    };
  }

  /* Scarcity (Lucas 2026-06-04 — dead simple, loss-aversion factuelle):
     Self: "Reveal your AstroSong before [date]. After that, the sky moves
            on, and so does its <em>[adjective]</em> energy."
     Gift: "Reveal [name]'s AstroSong before [date]. ..."
            (or "Reveal their AstroSong ..." if name is unknown)
     Pas de "Places are limited" (wallpaper). La VRAIE raison d'urgence est
     que la signature cosmique de l'event ne sera plus dans le ciel. */
  /* Scarcity (Lucas 2026-06-05 — final): factual loss + chrono in phrase 1,
     cosmic-gift emotional close in phrase 2 with imperative + reason.
     Single "AstroSong" mention in phrase 1 (uses who.person since the sub
     above already anchored the chart with the name); phrase 2 anaphores
     the AstroSong via "it" to avoid the lourdeur of repeating "AstroSong".
     "This gift the cosmos brought today" = the cadeau framing (cosmos as
     giver, today as temporal anchor) without negative semantics of "in
     your way". The em sits on the adjective in phrase 1 only — single em
     keeps the format consistent with the renderer that splits core1 + em
     + core2. */
  function formatScarcity(event, who) {
    who = ensureWho(who);
    var dateStr = formatDate(event.peak);
    return {
      core1: 'After ' + dateStr + ', ' + who.chart + ' AstroSong won’t carry this ' + (event.adjective || '') + ' energy. Reveal it now, ',
      em: 'before this cosmic gift fades',
      core2: '.'
    };
  }

  /* Sticky bar — "Before <em>[event phrase]</em>, [date]"
     Uses marker_title (stripped of its preposition) so the phrasing fits
     naturally after "Before": "Venus enters Leo" instead of "the Venus in Leo",
     "the Gemini New Moon" instead of just "Gemini New Moon", etc. */
  function formatStickyText(event) {
    var stripped = stripLeadingPreposition(event.marker_title || '');
    return {
      prefix: 'Before ',
      em: stripped.rest || event.marker_short || '',
      suffix: ', ' + formatDate(event.peak)
    };
  }

  function formatIntroEyebrow(event) {
    return 'Until ' + formatDate(event.peak);
  }

  /* Intro title (Lucas 2026-06-04 — clarté commerciale familière):
     "[Event marker_short] Special Offer — <em>50% off</em>"
     "Special Offer" = pattern DTC standard, instantanément compris par
     l'avatar US 45-65. Em gold sur le chiffre = focal visuel sur le
     bénéfice tangible. Le sacré vit dans le hero + scarcity. */
  function formatIntroTitle(event) {
    /* Nominalize verbal markers ("Venus enters Virgo" -> "Venus in Virgo")
       so the offer reads like a proper occasion label. */
    var evName = (event.marker_short || '').replace(/\senters\s/i, ' in ');
    return {
      core: evName + ' Special Offer — ',
      em: '50% OFF'
    };
  }

  /* Event-type → background image URLs (mobile + desktop masters).
     Filled in progressively as Lucas generates each image via Higgsfield.
     Missing types fall back to the CSS default (currently the new moon test
     image as placeholder, see .as-sales__hero in as-song-builder.css).
     URLs use the Shopify CDN and should be uploaded as
     `bg-{type}-mobile.webp` and `bg-{type}-desktop.webp`. */
  var EVENT_BG = {
    new_moon: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-new-moon-mobile.webp?v=1780553994',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-new-moon-desktop.webp?v=1780553618'
    },
    full_moon: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-full-moon-mobile.webp?v=1780573472',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-full-moon-desktop.webp?v=1780573468'
    },
    lunar_eclipse: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-lunar-eclipse-mobile.webp?v=1780581351',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-lunar-eclipse-desktop.webp?v=1780581348'
    },
    solar_eclipse: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-solar-eclipse-mobile.webp?v=1780583899',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-solar-eclipse-desktop.webp?v=1780583896'
    },
    /* Composite key "type_season" for solstice/equinox — each season has
       a distinct visual (warm summer twilight vs cold winter night, etc.). */
    solstice_summer: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-solstice-summer-mobile.webp?v=1780583893',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-solstice-summer-desktop.webp?v=1780583890'
    },
    solstice_winter: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-solstice-winter-mobile.webp?v=1780584476',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-solstice-winter-desktop.webp?v=1780584473'
    },
    equinox_spring: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-equinox-spring-mobile.webp?v=1780584961',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-equinox-spring-desktop.webp?v=1780584958'
    },
    equinox_autumn: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-equinox-autumn-mobile.webp?v=1780621372',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-equinox-autumn-desktop.webp?v=1780621367'
    },
    mercury_direct: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-mercury-direct-mobile.webp?v=1780622684',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-mercury-direct-desktop.webp?v=1780622681'
    },
    mercury_retrograde: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-mercury-retrograde-mobile.webp?v=1780622895',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-mercury-retrograde-desktop.webp?v=1780622890'
    },
    sun_enters: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-sun-enters-mobile.webp?v=1780623145',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-sun-enters-desktop.webp?v=1780623141'
    },
    venus_enters: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-venus-enters-mobile.webp?v=1780624016',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-venus-enters-desktop.webp?v=1780624013'
    },
    mars_enters: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-mars-enters-mobile.webp?v=1780624344',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-mars-enters-desktop.webp?v=1780624339'
    },
    jupiter_enters: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-jupiter-enters-mobile.webp?v=1780624769',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-jupiter-enters-desktop.webp?v=1780624764'
    },
    /* planet-generic — shared cosmic starfield BG for the 5 ingressions where the
       planet is not visible to the naked eye (saturn/mercury/uranus/neptune/pluto). */
    saturn_enters: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-planet-generic-mobile.webp?v=1780624989',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-planet-generic-desktop.webp?v=1780624985'
    },
    mercury_enters: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-planet-generic-mobile.webp?v=1780624989',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-planet-generic-desktop.webp?v=1780624985'
    },
    uranus_enters: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-planet-generic-mobile.webp?v=1780624989',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-planet-generic-desktop.webp?v=1780624985'
    },
    neptune_enters: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-planet-generic-mobile.webp?v=1780624989',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-planet-generic-desktop.webp?v=1780624985'
    },
    pluto_enters: {
      mobile: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-planet-generic-mobile.webp?v=1780624989',
      desktop: 'https://cdn.shopify.com/s/files/1/0947/1931/2167/files/event-bg-planet-generic-desktop.webp?v=1780624985'
    }
  };

  /* Apply the per-event background to the hero via CSS custom properties.
     Solstice/equinox use composite key "type_season" (e.g., solstice_summer).
     The CSS reads --hero-bg-mobile + --hero-bg-desktop and falls back to
     a default image if the event has no mapping entry yet. */
  function applyEventBg(event) {
    /* Backwards compat: accept either an event object or a plain type string */
    var key, evObj;
    if (typeof event === 'string') {
      key = event;
      evObj = null;
    } else {
      evObj = event || {};
      key = evObj.type || '';
      if ((evObj.type === 'solstice' || evObj.type === 'equinox') && evObj.season) {
        key = evObj.type + '_' + evObj.season;
      }
    }
    var bg = EVENT_BG[key];
    if (!bg) {
      /* Reset to default — leaves CSS fallback in charge */
      document.documentElement.style.removeProperty('--hero-bg-mobile');
      document.documentElement.style.removeProperty('--hero-bg-desktop');
      return;
    }
    document.documentElement.style.setProperty('--hero-bg-mobile', 'url("' + bg.mobile + '")');
    document.documentElement.style.setProperty('--hero-bg-desktop', 'url("' + bg.desktop + '")');
  }

  /* Announcement-bar text — short site-wide format:
       "✨ [Marker short] Sale — 50% off ends [smart-date]"
     "Sale" is the connective tissue that frames the event as the promo name.
     smart-date logic:
       <12h   → "tonight"
       <48h   → "tomorrow"
       <=3d   → "in N days"
       >3d    → "soon"
     No trailing period (Lucas — 2026-06-05).                              */
  function formatAnnouncementText(event) {
    if (!event) return '✨ Reveal your AstroSong — 50% off';
    var marker = event.marker_short || event.label || 'Cosmic event';
    var closesAt = event.closes ? new Date(event.closes).getTime() : null;
    if (!closesAt || isNaN(closesAt)) return '✨ ' + marker + ' Sale — 50% off';
    var now = Date.now();
    var hoursLeft = (closesAt - now) / 3600000;
    var daysLeft = hoursLeft / 24;
    var smartDate;
    if (hoursLeft < 12) smartDate = 'tonight';
    else if (hoursLeft < 48) smartDate = 'tomorrow';
    else if (daysLeft <= 3) smartDate = 'in ' + Math.ceil(daysLeft) + ' days';
    else smartDate = 'soon';
    return '✨ ' + marker + ' Sale — 50% off ends ' + smartDate;
  }

  /* Auto-patch any [data-astro-announcement-text] element with the active
     event copy. Runs at DOM ready (safe whether script is sync or defer). */
  function patchAnnouncementBar() {
    var els = document.querySelectorAll('[data-astro-announcement-text]');
    if (!els.length) return;
    var active = pickActive();
    var text = formatAnnouncementText(active);
    for (var i = 0; i < els.length; i++) {
      els[i].textContent = text;
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchAnnouncementBar);
  } else {
    patchAnnouncementBar();
  }

  /* Offer card pill — event-aware text inside the small pill under each card's
     sub ("Center Stage" design, Lucas 2026-06-06): "[event] · [adjective]
     energy". The leading star is added in CSS (::before). Same on all 3 cards;
     event name = marker_short, energy = adjective. */
  function formatCardEnergy(event) {
    var adj = (event && event.adjective) || '';
    /* Same nominalization as hero/offer: "Venus enters Virgo" -> "Venus in Virgo" */
    var marker = ((event && event.marker_short) || '').replace(/\senters\s/i, ' in ');
    return marker + ' · ' + adj + ' energy';
  }

  /* Slots counter — deterministic, progressive artisanal-capacity scarcity
     (Lucas 2026-06-06). Descent window = [previous event's peak -> this
     event's peak], i.e. the whole time the offer is on screen: it already
     moves while we market it, not only from the official "opens" date.
     Starts ~85 (a few taken, bar lightly filled), eases DOWN to a 3..7 floor
     at the peak, accelerating into it (last-minute rush). Pure function of
     real time -> monotonic, no reload bump, everyone sees the same number
     (social proof, not personal). Recomputed live on the 1s sticky tick. */
  function getSlots(event) {
    var TOTAL = 100;
    if (!event) return { left: 8, total: TOTAL, filledPct: 80 };
    var peakMs = Date.parse(event.peak);
    if (isNaN(peakMs)) return { left: 8, total: TOTAL, filledPct: 80 };

    /* Offer appears when the PREVIOUS event passes its peak — find the latest
       peak strictly before this one. */
    var prevPeakMs = NaN;
    for (var i = 0; i < allEvents.length; i++) {
      var p = Date.parse(allEvents[i].peak);
      if (!isNaN(p) && p < peakMs && (isNaN(prevPeakMs) || p > prevPeakMs)) prevPeakMs = p;
    }
    if (isNaN(prevPeakMs)) prevPeakMs = peakMs - 10 * 86400000;

    var span = peakMs - prevPeakMs;
    var progress = span > 0 ? (Date.now() - prevPeakMs) / span : 1;
    if (progress < 0) progress = 0;
    if (progress > 1) progress = 1;

    /* Deliberate small cap per event (doctrine: "we limit spots, so each gets
       our full attention"). Count descends MAX 12 -> MIN 3..5 (deterministic
       per event so the floor isn't identical each time). Ease-in (^1.6): slow
       start, accelerating rush near the peak. Fill bar FLOORED at 70% and tied
       to the count (12 left = 70%, MIN left = 97%) so it always reads "filling
       fast" and stays coherent with the number. Lucas 2026-06-06. */
    var START = 12;
    var END = 3 + (Math.abs(Math.floor(peakMs / 86400000)) % 3);
    var eased = Math.pow(progress, 1.6);
    var left = Math.round(START - (START - END) * eased);
    if (left < END) left = END;
    if (left > START) left = START;
    var range = START - END;
    var filledPct = range > 0 ? Math.round(70 + (START - left) / range * 27) : 85;

    return { left: left, total: TOTAL, filledPct: filledPct };
  }

  window.AstroWindow = {
    pickActive: pickActive,
    formatDate: formatDate,
    buildWho: buildWho,
    formatTitle: formatTitle,
    formatSubtitle: formatSubtitle,
    formatScarcity: formatScarcity,
    formatStickyText: formatStickyText,
    formatIntroEyebrow: formatIntroEyebrow,
    formatIntroTitle: formatIntroTitle,
    formatAnnouncementText: formatAnnouncementText,
    formatCardEnergy: formatCardEnergy,
    getSlots: getSlots,
    applyEventBg: applyEventBg,
    countEvents: function () { return allEvents.length; },
    _events: allEvents,
    _bg: EVENT_BG
  };
})();
